import { useCallback, useEffect, useRef, useState } from 'react'
import {
  GoogleMap,
  useJsApiLoader,
  Polygon,
  Polyline,
  Marker,
} from '@react-google-maps/api'
import type { Tables } from '@limo/database'
import type { Coordinate } from '@limo/shared/pricing'
import { Loader2, Maximize2, Minimize2, Pencil, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { GOOGLE_MAPS_LOADER_OPTIONS } from '@/lib/maps/loader'

type FlatRateZone = Tables<'flat_rate_zones'>

// Default map center (US center)
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }
const DEFAULT_ZOOM = 4

// Map container styles
const mapContainerStyle = {
  width: '100%',
  height: '400px',
}

const fullscreenMapContainerStyle = {
  width: '100%',
  height: '600px',
}

// Default polygon options
const polygonOptions = {
  fillOpacity: 0.3,
  strokeWeight: 2,
  editable: false,
  draggable: false,
}

const selectedPolygonOptions = {
  fillOpacity: 0.5,
  strokeWeight: 3,
  editable: true,
  draggable: false,
}

// Color used while a new zone is being drawn (before it is named/saved)
const DRAW_COLOR = '#3B82F6'

interface ZoneMapEditorProps {
  zones: FlatRateZone[]
  selectedZoneId: string | null
  onZoneCreate: (polygon: Coordinate[]) => void
  onZoneSelect: (zoneId: string | null) => void
  onZoneUpdate: (zoneId: string, polygon: Coordinate[]) => void
}

export function ZoneMapEditor({
  zones,
  selectedZoneId,
  onZoneCreate,
  onZoneSelect,
  onZoneUpdate,
}: ZoneMapEditorProps) {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const mapRef = useRef<google.maps.Map | null>(null)
  const polygonRefs = useRef<Map<string, google.maps.Polygon>>(new Map())

  // Custom polygon drawing state (replaces google.maps.drawing.DrawingManager,
  // which Google removed from the Maps JS API in v3.65 / unavailable May 2026).
  // We collect vertices in Google Maps (lat, lng) order while drawing, then
  // convert to the persisted GeoJSON [lng, lat] Coordinate[] format on finish.
  const [isDrawing, setIsDrawing] = useState(false)
  const [drawPoints, setDrawPoints] = useState<google.maps.LatLngLiteral[]>([])

  // Load Google Maps. Options MUST be identical to every other Builder
  // useJsApiLoader call site (see src/lib/maps/loader.ts) or the loader throws
  // "Loader must not be called again with different options".
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS)

  // Handle map load
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map

    // If we have zones, fit bounds to show all of them
    if (zones.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      zones.forEach((zone) => {
        const polygon = zone.polygon as Coordinate[]
        polygon.forEach(([lng, lat]) => {
          bounds.extend({ lat, lng })
        })
      })
      map.fitBounds(bounds, 50)
    }
  }, [zones])

  // Start drawing a new zone
  const startDrawing = useCallback(() => {
    onZoneSelect(null)
    setDrawPoints([])
    setIsDrawing(true)
  }, [onZoneSelect])

  // Cancel an in-progress drawing
  const cancelDrawing = useCallback(() => {
    setIsDrawing(false)
    setDrawPoints([])
  }, [])

  // Finish drawing and emit the zone in the persisted Coordinate[] format.
  // Format MUST match the previous DrawingManager output exactly:
  // GeoJSON [lng, lat] pairs, with the ring closed (first point repeated).
  const finishDrawing = useCallback(() => {
    if (drawPoints.length < 3) return

    const coordinates: Coordinate[] = drawPoints.map(
      ({ lat, lng }) => [lng, lat] as Coordinate
    )

    // Close the polygon
    if (coordinates.length > 0) {
      coordinates.push(coordinates[0])
    }

    setIsDrawing(false)
    setDrawPoints([])

    onZoneCreate(coordinates)
  }, [drawPoints, onZoneCreate])

  // Add a vertex on map click while drawing; otherwise clicking clears selection
  const onMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (isDrawing) {
        if (!e.latLng) return
        const point = { lat: e.latLng.lat(), lng: e.latLng.lng() }
        setDrawPoints((prev) => [...prev, point])
        return
      }
      onZoneSelect(null)
    },
    [isDrawing, onZoneSelect]
  )

  // Allow finishing by clicking the first vertex
  const onFirstVertexClick = useCallback(() => {
    if (isDrawing && drawPoints.length >= 3) {
      finishDrawing()
    }
  }, [isDrawing, drawPoints.length, finishDrawing])

  // Finish drawing on Enter, cancel on Escape (keyboard convenience)
  useEffect(() => {
    if (!isDrawing) return

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        finishDrawing()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        cancelDrawing()
      }
    }

    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isDrawing, finishDrawing, cancelDrawing])

  // Handle polygon click
  const onPolygonClick = useCallback(
    (zoneId: string) => {
      if (isDrawing) return
      onZoneSelect(zoneId === selectedZoneId ? null : zoneId)
    },
    [onZoneSelect, selectedZoneId, isDrawing]
  )

  // Handle polygon path change
  const onPolygonPathChange = useCallback(
    (zoneId: string, polygon: google.maps.Polygon) => {
      const path = polygon.getPath()
      const coordinates: Coordinate[] = []

      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i)
        coordinates.push([point.lng(), point.lat()])
      }

      // Close the polygon
      if (coordinates.length > 0) {
        coordinates.push(coordinates[0])
      }

      onZoneUpdate(zoneId, coordinates)
    },
    [onZoneUpdate]
  )

  // Store polygon ref and add path change listeners
  const onPolygonLoad = useCallback(
    (zoneId: string, polygon: google.maps.Polygon) => {
      polygonRefs.current.set(zoneId, polygon)

      // Add path change listeners for editing
      const path = polygon.getPath()
      google.maps.event.addListener(path, 'set_at', () => {
        onPolygonPathChange(zoneId, polygon)
      })
      google.maps.event.addListener(path, 'insert_at', () => {
        onPolygonPathChange(zoneId, polygon)
      })
      google.maps.event.addListener(path, 'remove_at', () => {
        onPolygonPathChange(zoneId, polygon)
      })
    },
    [onPolygonPathChange]
  )

  // Convert Coordinate[] to LatLngLiteral[]
  const coordinatesToLatLng = (coordinates: Coordinate[]) => {
    return coordinates.map(([lng, lat]) => ({ lat, lng }))
  }

  if (loadError) {
    return (
      <div className="h-[400px] flex items-center justify-center bg-muted/50 rounded-lg border">
        <div className="text-center text-muted-foreground">
          <p>Failed to load Google Maps</p>
          <p className="text-sm">Please check your API key configuration.</p>
        </div>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="h-[400px] flex items-center justify-center bg-muted/50 rounded-lg border">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const canFinish = drawPoints.length >= 3

  return (
    <div className="relative rounded-lg overflow-hidden border">
      <GoogleMap
        mapContainerStyle={isFullscreen ? fullscreenMapContainerStyle : mapContainerStyle}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        onLoad={onMapLoad}
        options={{
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          draggableCursor: isDrawing ? 'crosshair' : undefined,
          disableDoubleClickZoom: isDrawing,
        }}
        onClick={onMapClick}
      >
        {/* In-progress drawing: filled preview once we have 3+ vertices,
            otherwise a polyline so the first one or two clicks are visible. */}
        {isDrawing && drawPoints.length >= 3 && (
          <Polygon
            path={drawPoints}
            options={{
              fillColor: DRAW_COLOR,
              fillOpacity: 0.3,
              strokeColor: DRAW_COLOR,
              strokeWeight: 2,
              clickable: false,
              editable: false,
              draggable: false,
              zIndex: 10,
            }}
          />
        )}
        {isDrawing && drawPoints.length >= 1 && drawPoints.length < 3 && (
          <Polyline
            path={drawPoints}
            options={{
              strokeColor: DRAW_COLOR,
              strokeWeight: 2,
              clickable: false,
              zIndex: 10,
            }}
          />
        )}

        {/* Vertex markers while drawing. Clicking the first vertex closes
            the polygon (standard drawing UX). */}
        {isDrawing &&
          drawPoints.map((point, index) => (
            <Marker
              key={`draw-vertex-${index}`}
              position={point}
              onClick={index === 0 ? onFirstVertexClick : undefined}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: index === 0 && canFinish ? 7 : 5,
                fillColor: '#ffffff',
                fillOpacity: 1,
                strokeColor: DRAW_COLOR,
                strokeWeight: 2,
              }}
              zIndex={20}
            />
          ))}

        {/* Existing Zones (hidden while drawing to avoid click conflicts) */}
        {!isDrawing &&
          zones.map((zone) => {
            const isSelected = zone.id === selectedZoneId
            const polygon = zone.polygon as Coordinate[]

            return (
              <Polygon
                key={zone.id}
                path={coordinatesToLatLng(polygon)}
                options={{
                  ...(isSelected ? selectedPolygonOptions : polygonOptions),
                  fillColor: zone.color,
                  strokeColor: zone.color,
                }}
                onClick={() => onPolygonClick(zone.id)}
                onLoad={(p) => onPolygonLoad(zone.id, p)}
              />
            )
          })}
      </GoogleMap>

      {/* Drawing controls (top center) */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {!isDrawing ? (
          <Button variant="secondary" size="sm" onClick={startDrawing}>
            <Pencil className="h-4 w-4 mr-1.5" />
            Draw Zone
          </Button>
        ) : (
          <>
            <Button
              variant="default"
              size="sm"
              onClick={finishDrawing}
              disabled={!canFinish}
            >
              <Check className="h-4 w-4 mr-1.5" />
              Finish ({drawPoints.length})
            </Button>
            <Button variant="secondary" size="sm" onClick={cancelDrawing}>
              <X className="h-4 w-4 mr-1.5" />
              Cancel
            </Button>
          </>
        )}
      </div>

      {/* Fullscreen toggle */}
      <Button
        variant="secondary"
        size="icon"
        className="absolute top-2 right-2"
        onClick={() => setIsFullscreen(!isFullscreen)}
      >
        {isFullscreen ? (
          <Minimize2 className="h-4 w-4" />
        ) : (
          <Maximize2 className="h-4 w-4" />
        )}
      </Button>

      {/* Instructions overlay */}
      <div className="absolute bottom-2 left-2 bg-background/90 px-3 py-1.5 rounded-md text-xs text-muted-foreground max-w-[90%]">
        {isDrawing ? (
          <>
            Click on the map to add points. Add at least 3, then click{' '}
            <span className="font-medium">Finish</span> (or the first point, or
            press Enter). Press Esc to cancel.
          </>
        ) : (
          <>
            Draw: Click <span className="font-medium">Draw Zone</span>, then
            click on the map to add boundary points. Edit: Click a zone to select
            it, then drag its vertices.
          </>
        )}
      </div>
    </div>
  )
}
